import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  EditorContent,
  useEditor,
  BubbleMenu,
  FloatingMenu,
  Editor,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Typography from '@tiptap/extension-typography';
import {
  AlignCenter,
  AlignLeft,
  Bold,
  Code,
  CodeIcon,
  Expand,
  Heading1,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Minus,
  Pilcrow,
  Plus,
  Quote,
  Share2,
  Strikethrough,
  Trash2,
} from 'lucide-react';

import MediaPickerDialog from './MediaPickerDialog';
import AssetImage, { AssetImageAlign } from './extensions/AssetImage';
import Embed from './extensions/Embed';
import { parseEmbedUrl } from './embedProviders';
import { CMSAsset } from '@/client/types/cms';

interface RichEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /** When true, removes outer card chrome — used inside the page editor. */
  bare?: boolean;
  className?: string;
  /** Existing page id — used to attach inline assets to the page roster. */
  cmsoid?: number | null;
  /** Called after an inline image with asid is inserted (parent attaches via API). */
  onAttachAsset?: (asid: number) => void;
}

// ─── Slash command options ───────────────────────────────────────────────────

interface SlashItem {
  id: string;
  label: string;
  hint: string;
  icon: React.ComponentType<{ className?: string }>;
  match: string[];
  run: (editor: Editor) => void;
}

const buildSlashItems = (
  openMediaPicker: () => void,
  openEmbedPrompt: () => void,
): SlashItem[] => [
  {
    id: 'h1',
    label: 'Heading 1',
    hint: 'Big section heading',
    icon: Heading1,
    match: ['h1', 'heading', 'title'],
    run: (e) => e.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    id: 'h2',
    label: 'Heading 2',
    hint: 'Medium section heading',
    icon: Heading2,
    match: ['h2', 'heading', 'subtitle'],
    run: (e) => e.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    id: 'h3',
    label: 'Heading 3',
    hint: 'Small section heading',
    icon: Heading3,
    match: ['h3', 'heading'],
    run: (e) => e.chain().focus().toggleHeading({ level: 3 }).run(),
  },
  {
    id: 'p',
    label: 'Paragraph',
    hint: 'Plain text',
    icon: Pilcrow,
    match: ['p', 'paragraph', 'text'],
    run: (e) => e.chain().focus().setParagraph().run(),
  },
  {
    id: 'ul',
    label: 'Bulleted list',
    hint: 'A simple list',
    icon: List,
    match: ['list', 'bullet', 'ul'],
    run: (e) => e.chain().focus().toggleBulletList().run(),
  },
  {
    id: 'ol',
    label: 'Numbered list',
    hint: 'List with order',
    icon: ListOrdered,
    match: ['list', 'numbered', 'ol', 'ordered'],
    run: (e) => e.chain().focus().toggleOrderedList().run(),
  },
  {
    id: 'quote',
    label: 'Quote',
    hint: 'Capture a quote',
    icon: Quote,
    match: ['quote', 'blockquote'],
    run: (e) => e.chain().focus().toggleBlockquote().run(),
  },
  {
    id: 'code',
    label: 'Code block',
    hint: 'Monospaced code',
    icon: CodeIcon,
    match: ['code', 'pre'],
    run: (e) => e.chain().focus().toggleCodeBlock().run(),
  },
  {
    id: 'hr',
    label: 'Divider',
    hint: 'Horizontal rule',
    icon: Minus,
    match: ['hr', 'divider', 'rule', 'line'],
    run: (e) => e.chain().focus().setHorizontalRule().run(),
  },
  {
    id: 'image',
    label: 'Image',
    hint: 'Insert from media library',
    icon: ImageIcon,
    match: ['image', 'photo', 'picture', 'media', 'asset'],
    run: () => openMediaPicker(),
  },
  {
    id: 'embed',
    label: 'Embed',
    hint: 'YouTube, Vimeo, X, Instagram, TikTok',
    icon: Share2,
    match: ['embed', 'youtube', 'vimeo', 'twitter', 'x', 'instagram', 'tiktok', 'video', 'social'],
    run: () => openEmbedPrompt(),
  },
];

// ─── Slash command popup ─────────────────────────────────────────────────────

interface SlashPopupProps {
  editor: Editor;
  items: SlashItem[];
  onClose: () => void;
}

const SlashPopup = ({ editor, items, onClose }: SlashPopupProps) => {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    const { from } = editor.state.selection;
    try {
      const c = editor.view.coordsAtPos(from);
      const editorRect = editor.view.dom.getBoundingClientRect();
      setCoords({
        top: c.bottom - editorRect.top + 6,
        left: c.left - editorRect.left,
      });
    } catch {
      /* ignore */
    }
  }, [editor]);

  useEffect(() => {
    const dom = editor.view.dom;

    const filtered = () =>
      items.filter((it) =>
        !query
          ? true
          : it.label.toLowerCase().includes(query.toLowerCase()) ||
            it.match.some((m) => m.includes(query.toLowerCase())),
      );

    const handler = (e: KeyboardEvent) => {
      const results = filtered();
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % Math.max(results.length, 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + Math.max(results.length, 1)) % Math.max(results.length, 1));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const pick = results[activeIdx];
        if (pick) {
          const { from } = editor.state.selection;
          editor
            .chain()
            .focus()
            .deleteRange({ from: from - query.length - 1, to: from })
            .run();
          pick.run(editor);
        }
        onClose();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Backspace') {
        if (query.length === 0) onClose();
        else setQuery((q) => q.slice(0, -1));
        return;
      }
      if (e.key.length === 1 && !e.metaKey && !e.ctrlKey) {
        setQuery((q) => q + e.key);
        setActiveIdx(0);
      }
    };

    dom.addEventListener('keydown', handler);
    return () => dom.removeEventListener('keydown', handler);
  }, [editor, items, query, activeIdx, onClose]);

  const filtered = items.filter((it) =>
    !query
      ? true
      : it.label.toLowerCase().includes(query.toLowerCase()) ||
        it.match.some((m) => m.includes(query.toLowerCase())),
  );

  if (!coords || filtered.length === 0) return null;

  return (
    <div
      className="absolute z-30 w-72 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
      style={{ top: coords.top, left: coords.left }}
    >
      <div className="border-b px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-400">
        {query ? `Filter: ${query}` : 'Insert block'}
      </div>
      <div className="max-h-72 overflow-y-auto py-1">
        {filtered.map((it, idx) => {
          const Icon = it.icon;
          const active = idx === activeIdx;
          return (
            <button
              key={it.id}
              type="button"
              onMouseEnter={() => setActiveIdx(idx)}
              onMouseDown={(e) => {
                e.preventDefault();
                const { from } = editor.state.selection;
                editor
                  .chain()
                  .focus()
                  .deleteRange({ from: from - query.length - 1, to: from })
                  .run();
                it.run(editor);
                onClose();
              }}
              className={`flex w-full items-start gap-3 px-3 py-2 text-left transition-colors ${
                active ? 'bg-blue-50' : 'hover:bg-gray-50'
              }`}
            >
              <span
                className={`flex h-8 w-8 items-center justify-center rounded border ${
                  active ? 'border-blue-200 bg-white' : 'border-gray-200 bg-white'
                }`}
              >
                <Icon className={`h-4 w-4 ${active ? 'text-blue-600' : 'text-gray-600'}`} />
              </span>
              <span className="flex flex-col">
                <span className="text-sm font-medium text-gray-900">{it.label}</span>
                <span className="text-xs text-gray-500">{it.hint}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main editor component ───────────────────────────────────────────────────

const RichEditor = ({
  value,
  onChange,
  placeholder = 'Tell your story…',
  bare,
  className,
  cmsoid: _cmsoid,
  onAttachAsset,
}: RichEditorProps) => {
  const [showSlash, setShowSlash] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const replacingImageRef = useRef(false);
  const onAttachAssetRef = useRef(onAttachAsset);
  onAttachAssetRef.current = onAttachAsset;

  const openEmbedPrompt = useCallback((editor: Editor | null) => {
    if (!editor) return;
    const raw = window.prompt('Paste a YouTube, Vimeo, X, Instagram, or TikTok URL');
    if (!raw) return;
    const parsed = parseEmbedUrl(raw);
    if (!parsed) {
      window.alert('That URL is not a supported embed.');
      return;
    }
    editor
      .chain()
      .focus()
      .setEmbed({
        src: parsed.src,
        provider: parsed.provider,
        originalUrl: parsed.originalUrl,
      })
      .run();
  }, []);

  const slashItems = useMemo(
    () =>
      buildSlashItems(
        () => setShowMediaPicker(true),
        () => openEmbedPrompt(editorRef.current),
      ),
    [openEmbedPrompt],
  );

  const editorRef = useRef<Editor | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        codeBlock: { HTMLAttributes: { class: 'cms-code-block' } },
        blockquote: { HTMLAttributes: { class: 'cms-blockquote' } },
      }),
      Placeholder.configure({
        placeholder: ({ node }) => {
          if (node.type.name === 'heading') return 'Heading';
          return placeholder;
        },
        emptyEditorClass: 'is-editor-empty',
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      AssetImage,
      Embed,
      Typography,
    ],
    content: value,
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'cms-tt-editor focus:outline-none',
      },
      handleKeyDown: (_view, event) => {
        if (event.key === '/' && !showSlash) {
          const { $from } = (editorRef.current?.state.selection ?? {}) as any;
          const isEmpty = $from?.parent?.content?.size === 0;
          if (isEmpty) setShowSlash(true);
        }
        return false;
      },
      handlePaste: (_view, event) => {
        const text = event.clipboardData?.getData('text/plain')?.trim();
        if (!text || /\s/.test(text)) return false;
        const parsed = parseEmbedUrl(text);
        if (!parsed || !editorRef.current) return false;
        event.preventDefault();
        editorRef.current
          .chain()
          .focus()
          .setEmbed({
            src: parsed.src,
            provider: parsed.provider,
            originalUrl: parsed.originalUrl,
          })
          .run();
        return true;
      },
    },
  });

  editorRef.current = editor;

  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) editor.commands.setContent(value || '', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const insertAssetImage = (asset: CMSAsset) => {
    if (!asset.public_url || !editor) return;
    const asid = asset.asid != null ? Number(asset.asid) : null;
    if (replacingImageRef.current && editor.isActive('assetImage')) {
      editor
        .chain()
        .focus()
        .updateAssetImage({
          src: asset.public_url,
          alt: asset.alt_text ?? '',
          asid,
        })
        .run();
    } else {
      editor
        .chain()
        .focus()
        .setAssetImage({
          src: asset.public_url,
          alt: asset.alt_text ?? '',
          asid,
          align: 'center',
          caption: null,
        })
        .run();
    }
    replacingImageRef.current = false;
    setShowMediaPicker(false);
    if (asid != null && Number.isFinite(asid)) {
      onAttachAssetRef.current?.(asid);
    }
  };

  const setAlign = (align: AssetImageAlign) => {
    editor?.chain().focus().updateAssetImage({ align }).run();
  };

  if (!editor) {
    return <div className={`h-40 ${bare ? '' : 'border rounded-md'} animate-pulse bg-gray-50`} />;
  }

  const imageActive = editor.isActive('assetImage');
  const imageAttrs = imageActive ? editor.getAttributes('assetImage') : null;

  return (
    <div className={`relative ${className ?? ''}`}>
      <BubbleMenu
        editor={editor}
        tippyOptions={{ duration: 100, placement: 'top' }}
        shouldShow={({ editor: ed }) => {
          const { empty } = ed.state.selection;
          return (!empty && !ed.isActive('assetImage') && !ed.isActive('embed')) || ed.isActive('assetImage');
        }}
        className="flex items-center gap-0.5 rounded-md border border-gray-200 bg-white px-1 py-1 shadow-md"
      >
        {imageActive ? (
          <>
            <BubbleBtn
              icon={AlignLeft}
              onClick={() => setAlign('left')}
              isActive={imageAttrs?.align === 'left'}
              label="Align left"
            />
            <BubbleBtn
              icon={AlignCenter}
              onClick={() => setAlign('center')}
              isActive={imageAttrs?.align === 'center' || !imageAttrs?.align}
              label="Align center"
            />
            <BubbleBtn
              icon={Expand}
              onClick={() => setAlign('wide')}
              isActive={imageAttrs?.align === 'wide'}
              label="Wide"
            />
            <span className="mx-1 h-5 w-px bg-gray-200" />
            <BubbleBtn
              icon={ImageIcon}
              onClick={() => {
                const alt = window.prompt('Alt text', imageAttrs?.alt ?? '') ?? null;
                if (alt === null) return;
                editor.chain().focus().updateAssetImage({ alt }).run();
              }}
              label="Alt text"
            />
            <BubbleBtn
              icon={Quote}
              onClick={() => {
                const caption =
                  window.prompt('Caption', imageAttrs?.caption ?? '') ?? null;
                if (caption === null) return;
                editor.chain().focus().updateAssetImage({ caption: caption || null }).run();
              }}
              label="Caption"
            />
            <BubbleBtn
              icon={ImageIcon}
              onClick={() => {
                replacingImageRef.current = true;
                setShowMediaPicker(true);
              }}
              label="Replace image"
            />
            <BubbleBtn
              icon={Trash2}
              onClick={() => editor.chain().focus().deleteSelection().run()}
              label="Remove"
            />
          </>
        ) : (
          <>
            <BubbleBtn
              icon={Bold}
              onClick={() => editor.chain().focus().toggleBold().run()}
              isActive={editor.isActive('bold')}
              label="Bold"
            />
            <BubbleBtn
              icon={Italic}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              isActive={editor.isActive('italic')}
              label="Italic"
            />
            <BubbleBtn
              icon={Strikethrough}
              onClick={() => editor.chain().focus().toggleStrike().run()}
              isActive={editor.isActive('strike')}
              label="Strike"
            />
            <BubbleBtn
              icon={Code}
              onClick={() => editor.chain().focus().toggleCode().run()}
              isActive={editor.isActive('code')}
              label="Inline code"
            />
            <span className="mx-1 h-5 w-px bg-gray-200" />
            <BubbleBtn
              icon={LinkIcon}
              onClick={() => {
                const previous = editor.getAttributes('link').href ?? '';
                const url = window.prompt('Link URL', previous);
                if (url === null) return;
                if (url === '') {
                  editor.chain().focus().extendMarkRange('link').unsetLink().run();
                  return;
                }
                editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
              }}
              isActive={editor.isActive('link')}
              label="Link"
            />
            <BubbleBtn
              icon={Heading2}
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              isActive={editor.isActive('heading', { level: 2 })}
              label="Heading"
            />
            <BubbleBtn
              icon={Quote}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              isActive={editor.isActive('blockquote')}
              label="Quote"
            />
          </>
        )}
      </BubbleMenu>

      <FloatingMenu
        editor={editor}
        tippyOptions={{ duration: 100, placement: 'left' }}
        className="flex items-center"
      >
        <button
          type="button"
          onClick={() => setShowSlash(true)}
          className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-500 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-900"
          aria-label="Insert block"
          title="Insert block (or type / )"
        >
          <Plus className="h-4 w-4" />
        </button>
      </FloatingMenu>

      <div className={bare ? '' : 'rounded-lg border border-gray-200 bg-white p-6 shadow-sm'}>
        <EditorContent editor={editor} />
      </div>

      {showSlash && (
        <SlashPopup editor={editor} items={slashItems} onClose={() => setShowSlash(false)} />
      )}

      <MediaPickerDialog
        open={showMediaPicker}
        onClose={() => {
          replacingImageRef.current = false;
          setShowMediaPicker(false);
        }}
        onSelect={insertAssetImage}
      />
    </div>
  );
};

// ─── Bubble menu button ──────────────────────────────────────────────────────

interface BubbleBtnProps {
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  isActive?: boolean;
  label: string;
}

const BubbleBtn = ({ icon: Icon, onClick, isActive, label }: BubbleBtnProps) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className={`flex h-7 w-7 items-center justify-center rounded transition-colors ${
      isActive
        ? 'bg-blue-100 text-blue-700'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`}
  >
    <Icon className="h-3.5 w-3.5" />
  </button>
);

export default RichEditor;
